"use client";

import React, { memo, useEffect, useMemo, useState } from "react";
import equal from "fast-deep-equal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedditAlien, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { Clock3, ExternalLink, Newspaper } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";

interface Source {
  text: string;
  author: string;
  score: number | null;
  url: string;
  post_title?: string;
  created_at: string;
  source_type: string;
  originalIndex?: number;
  [key: string]: any;
}

interface SourceDisplayProps {
  sources: Source[];
  highlightedSourceIndex?: number | null;
}

const SOURCE_TYPE_ORDER = ["news", "twitter", "reddit"];

const normalizeSourceType = (sourceType: string) =>
  String(sourceType || "").trim().toLowerCase();

const parseSourceDate = (dateString: string) => {
  const raw = String(dateString || "").trim();
  if (!raw) return null;
  const normalized = raw.endsWith("Z") ? raw.slice(0, -1) + "+00:00" : raw;
  const date = new Date(normalized);
  if (Number.isNaN(date.getTime())) return null;
  return date;
};

const formatExactTimestamp = (date: Date) =>
  date.toLocaleString("en-US", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
    timeZoneName: "short",
  });

const formatRelativeTimestamp = (date: Date, nowMs: number) => {
  const diffMs = Math.max(0, nowMs - date.getTime());
  const diffSeconds = Math.floor(diffMs / 1000);

  if (diffSeconds < 45) return "just now";
  if (diffSeconds < 3600) return `${Math.floor(diffSeconds / 60)}m ago`;
  if (diffSeconds < 86400) return `${Math.floor(diffSeconds / 3600)}h ago`;
  if (diffSeconds < 604800) return `${Math.floor(diffSeconds / 86400)}d ago`;
  if (diffSeconds < 2592000) return `${Math.floor(diffSeconds / 604800)}w ago`;
  return formatExactTimestamp(date);
};

const extractRedditInfo = (url: string) => {
  const match = url.match(/reddit\.com\/r\/([^\/]+)/);
  const subreddit = match ? match[1] : null;
  return { subreddit };
};

const extractHostname = (url: string) => {
  try {
    const hostname = new URL(url).hostname.replace(/^www\./, "");
    return hostname || null;
  } catch {
    return null;
  }
};

const getSourceTypeLabel = (sourceType: string) => {
  if (sourceType === "twitter") return "Twitter";
  if (sourceType === "reddit") return "Reddit";
  if (sourceType === "news") return "News";
  return sourceType || "Source";
};

const getPreviewText = (source: Source) => {
  const text = String(source.text || "").replace(/\s+/g, " ").trim();
  if (!text) return "";

  const maxChars =
    source.source_type === "news"
      ? 420
      : source.source_type === "twitter"
        ? 560
        : 480;

  return text.length > maxChars ? `${text.slice(0, maxChars).trimEnd()}...` : text;
};

function SourceDisplayComponent({
  sources,
  highlightedSourceIndex,
}: SourceDisplayProps) {
  const [nowMs, setNowMs] = useState<number | null>(null);
  const [activeSourceType, setActiveSourceType] = useState<string>("");

  useEffect(() => {
    setNowMs(Date.now());
    const intervalId = window.setInterval(() => {
      setNowMs(Date.now());
    }, 60000);

    return () => window.clearInterval(intervalId);
  }, []);

  const normalizedSources = useMemo(
    () =>
      sources.map((source) => ({
        ...source,
        source_type: normalizeSourceType(source.source_type),
      })),
    [sources]
  );

  const sourcesByType = useMemo(() => {
    return normalizedSources.reduce<Record<string, Source[]>>((acc, source) => {
      if (!acc[source.source_type]) {
        acc[source.source_type] = [];
      }
      acc[source.source_type].push(source);
      return acc;
    }, {});
  }, [normalizedSources]);

  const availableSourceTypes = useMemo(() => {
    const knownTypes = SOURCE_TYPE_ORDER.filter((sourceType) =>
      sourcesByType[sourceType]?.length
    );
    const otherTypes = Object.keys(sourcesByType)
      .filter((sourceType) => !SOURCE_TYPE_ORDER.includes(sourceType))
      .sort();
    return [...knownTypes, ...otherTypes];
  }, [sourcesByType]);

  const highlightedSourceType = useMemo(() => {
    if (highlightedSourceIndex == null) return null;
    return (
      normalizedSources.find(
        (source) => source.originalIndex === highlightedSourceIndex
      )?.source_type || null
    );
  }, [highlightedSourceIndex, normalizedSources]);

  const availableSourceTypesKey = availableSourceTypes.join("|");

  useEffect(() => {
    if (
      highlightedSourceType &&
      availableSourceTypes.includes(highlightedSourceType)
    ) {
      setActiveSourceType(highlightedSourceType);
      return;
    }

    setActiveSourceType((prev) => {
      if (prev && availableSourceTypes.includes(prev)) {
        return prev;
      }
      return availableSourceTypes[0] || "";
    });
  }, [availableSourceTypes, availableSourceTypesKey, highlightedSourceType]);

  const renderSourceTypeIcon = (sourceType: string, className?: string) => {
    if (sourceType === "twitter") {
      return <FontAwesomeIcon icon={faXTwitter} className={className} />;
    }
    if (sourceType === "reddit") {
      return <FontAwesomeIcon icon={faRedditAlien} className={className} />;
    }
    if (sourceType === "news") {
      return <Newspaper className={className} strokeWidth={2} />;
    }
    return null;
  };

  const renderSimpleSource = (source: Source, index: number) => {
    const { subreddit } = extractRedditInfo(source.url);
    const isHighlighted = highlightedSourceIndex === source.originalIndex;
    const parsedDate = parseSourceDate(source.created_at);
    const exactTimestamp = parsedDate
      ? formatExactTimestamp(parsedDate)
      : String(source.created_at || "Unknown");
    const timestampLabel =
      parsedDate && nowMs != null
        ? formatRelativeTimestamp(parsedDate, nowMs)
        : exactTimestamp;
    const sourceLabel = (() => {
      if (source.source_type === "reddit") {
        if (subreddit && source.author) return `r/${subreddit} · u/${source.author}`;
        if (subreddit) return `r/${subreddit}`;
        if (source.author) return `u/${source.author}`;
        return "Reddit";
      }
      if (source.source_type === "twitter") {
        return source.author ? `@${source.author}` : "X";
      }
      return source.author || extractHostname(source.url) || "News";
    })();
    const title =
      source.post_title ||
      (source.source_type === "twitter"
        ? `Post by @${source.author || "unknown"}`
        : source.source_type === "reddit"
          ? `Post from ${subreddit ? `r/${subreddit}` : "Reddit"}`
          : `Source ${index + 1}`);
    const previewText = getPreviewText(source);

    return (
      <a
        key={`${source.source_type}-${source.originalIndex ?? index}`}
        href={source.url}
        target="_blank"
        rel="noopener noreferrer"
        data-source-index={source.originalIndex}
        className={cn(
          "block rounded-xl border border-border/80 bg-background/30 p-4 transition-all duration-200 hover:border-border hover:bg-muted/20",
          isHighlighted &&
            "border-foreground shadow-[0_0_24px_hsl(var(--foreground)/0.18)]"
        )}
      >
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/30">
            {renderSourceTypeIcon(
              source.source_type,
              cn(
                "h-4 w-4",
                source.source_type === "twitter" && "text-foreground",
                source.source_type === "reddit" && "text-orange-500",
                source.source_type === "news" && "text-emerald-400"
              )
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-foreground break-words">
                  {title}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                  <span className="break-words">{sourceLabel}</span>
                  <span className="h-1 w-1 rounded-full bg-muted-foreground/50" />
                  <span
                    className="inline-flex items-center gap-1"
                    title={exactTimestamp}
                  >
                    <Clock3 className="h-3 w-3" />
                    {timestampLabel}
                  </span>
                </div>
              </div>

              <div className="inline-flex shrink-0 items-center gap-1 text-[11px] text-muted-foreground">
                Open
                <ExternalLink className="h-3 w-3" />
              </div>
            </div>

            {previewText && (
              <div
                className={cn(
                  "mt-3 whitespace-pre-wrap break-words text-sm leading-6 text-foreground/90",
                  source.source_type === "news" && "line-clamp-4",
                  source.source_type === "twitter" && "line-clamp-6",
                  source.source_type === "reddit" && "line-clamp-5"
                )}
              >
                {previewText}
              </div>
            )}
          </div>
        </div>
      </a>
    );
  };

  const renderDefaultSource = (source: Source, index: number) => {
    return (
      <div key={index} className="p-2 border rounded text-sm">
        <div className="font-medium">Source {index + 1}</div>
        <div className="border-border0 mt-1">
          Type: {source.source_type || "Unknown"}
        </div>
        {Object.entries(source).map(
          ([key, value]) =>
            key !== "source_type" && (
              <div key={key} className="text-xs text-muted-foreground mt-1">
                <span className="font-medium">{key}:</span> {String(value)}
              </div>
            )
        )}
      </div>
    );
  };

  const visibleSources =
    activeSourceType && sourcesByType[activeSourceType]
      ? sourcesByType[activeSourceType]
      : normalizedSources;

  return (
    <div className="space-y-3 overflow-hidden">
      {availableSourceTypes.length > 1 && activeSourceType && (
        <Tabs
          value={activeSourceType}
          onValueChange={setActiveSourceType}
          className="w-full"
        >
          <TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-xl bg-muted/30 p-1">
            {availableSourceTypes.map((sourceType) => (
              <TabsTrigger
                key={sourceType}
                value={sourceType}
                className="h-9 gap-2 rounded-lg px-3 text-xs font-medium"
              >
                {renderSourceTypeIcon(
                  sourceType,
                  cn(
                    "h-3.5 w-3.5",
                    sourceType === "twitter" && "text-foreground",
                    sourceType === "reddit" && "text-orange-500",
                    sourceType === "news" && "text-emerald-400"
                  )
                )}
                <span>{getSourceTypeLabel(sourceType)}</span>
                <span className="rounded-full border border-border/70 px-1.5 py-0.5 text-[10px] leading-none text-muted-foreground">
                  {sourcesByType[sourceType]?.length || 0}
                </span>
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      <div className="space-y-3 overflow-hidden">
        {visibleSources.map((source, index) => {
          if (
            source.source_type === "reddit" ||
            source.source_type === "twitter" ||
            source.source_type === "news"
          ) {
            return renderSimpleSource(source, index);
          }
          return renderDefaultSource(source, index);
        })}
      </div>
    </div>
  );
}

// Memoize with deep equality check to prevent re-renders when sources haven't changed
export const SourceDisplay = memo(SourceDisplayComponent, (prevProps, nextProps) => {
  return (
    equal(prevProps.sources, nextProps.sources) &&
    prevProps.highlightedSourceIndex === nextProps.highlightedSourceIndex
  );
});
